import * as Yup from 'yup';

const requiredMessage = `This field is required`;
const cdnaPatternMessage = `Please enter a valid cDNA`;
const cdnaDuplicateMessage = `Duplicate cDNA detected - please check for copy-paste errors`;

/**
 * HGVS cDNA validation based on https://hgvs-nomenclature.org/stable/recommendations/DNA/
 *
 * ============================================================================
 * SUBSTITUTION (https://hgvs-nomenclature.org/stable/recommendations/DNA/substitution/)
 * ============================================================================
 * Definition: a sequence change where, compared to a reference sequence, one nucleotide
 * is replaced by one other nucleotide.
 *
 * Format: "prefix""position_substituted""reference_nucleotide">"new_nucleotide"
 *
 * Examples:
 * - c.93G>T: a substitution of the G nucleotide at position 93 by a T
 * - c.79_80delinsTT: is not a substitution, it is an indel (deletion-insertion)
 * - c.93G>A: a substitution of the G nucleotide at position 93 by an A
 * - c.93G>C: a substitution of the G nucleotide at position 93 by a C
 * - c.93G>T: a substitution of the G nucleotide at position 93 by a T
 * - c.93+1G>T: a substitution of the G nucleotide at position 93+1 (intronic) by a T
 * - c.93-1G>T: a substitution of the G nucleotide at position 93-1 (intronic) by a T
 * - c.*96G>A: a substitution of the G nucleotide at position *96 (3' UTR) by an A
 * - c.-14G>A: a substitution of the G nucleotide at position -14 (5' UTR) by an A
 *
 * Notes:
 * - all substitutions are described at the DNA level, not at the RNA or protein level
 * - a substitution replaces exactly one nucleotide with exactly one other nucleotide
 * - nucleotides that have been tested and found not changed are described as c.93G=
 *
 * ============================================================================
 * DELETION (https://hgvs-nomenclature.org/stable/recommendations/DNA/deletion/)
 * ============================================================================
 * Definition: a sequence change where, compared to a reference sequence, one or more
 * nucleotides are not present (deleted).
 *
 * Format: "prefix""position(s)_deleted""del"
 *
 * Examples:
 * - c.10del: a deletion of the T at position 10 in the sequence GATGATTTGT to GATGATTGT
 * - c.6_8del: a deletion of nucleotides 6 to 8 (GAT) in the sequence ACTTGATGAT to ACTTGAT
 * - c.6_8delGAT: a deletion of nucleotides 6 to 8 (GAT) - with deleted sequence specified
 * - c.(4071+1_4072-1)_(5154+1_5155-1)del: a deletion of exon 30 (exon 30 starts at position
 *   4072, ends at position 5154)
 * - c.(?_-30)_(12+1_13-1)del: a deletion starting somewhere upstream of position -30 and
 *   ending somewhere between positions 12+1 and 13-1
 * - c.(?_-1)_(*1_?)del: a deletion of the entire coding sequence
 *
 * Notes:
 * - "del" is the deletion designation
 * - for all descriptions, the most 3' position possible is arbitrarily assigned to have
 *   been changed (3' rule)
 * - the deleted nucleotide(s) may be listed after "del" but this is not required
 *
 * ============================================================================
 * DUPLICATION (https://hgvs-nomenclature.org/stable/recommendations/DNA/duplication/)
 * ============================================================================
 * Definition: a sequence change where, compared to a reference sequence, a copy of one
 * or more nucleotides is inserted directly 3' of the original copy of that sequence.
 *
 * Format: "prefix""position(s)_duplicated""dup"
 *
 * Examples:
 * - c.7dup: a duplication of the T at position 7 in the sequence ACTTGTGCC to ACTTGTTGCC
 * - c.6_8dup: a duplication of nucleotides 6 to 8 (TGT) in the sequence ACTTGTGCC to
 *   ACTTGTGTGCC
 * - c.6_8dupTGT: a duplication of nucleotides 6 to 8 (TGT) - with duplicated sequence specified
 * - c.(4071+1_4072-1)_(5154+1_5155-1)dup: a duplication of exon 30
 *
 * Notes:
 * - "dup" is the duplication designation
 * - for all descriptions, the most 3' position possible is arbitrarily assigned to have
 *   been changed (3' rule)
 * - the duplicated nucleotide(s) may be listed after "dup" but this is not required
 * - duplications are described as insertions when the position of the insertion is not
 *   directly 3' of the original copy
 *
 * ============================================================================
 * INSERTION (https://hgvs-nomenclature.org/stable/recommendations/DNA/insertion/)
 * ============================================================================
 * Definition: a sequence change where, compared to a reference sequence, one or more
 * nucleotides are inserted and where the insertion is not a copy of a sequence
 * immediately 5'.
 *
 * Format: "prefix""positions_flanking""ins""inserted_sequence"
 *
 * Examples:
 * - c.5_6insT: an insertion of a T between positions 5 and 6
 * - c.5_6insGTCA: an insertion of GTCA between positions 5 and 6
 * - c.5_6ins10: an insertion of 10 nucleotides between positions 5 and 6
 * - c.5_6insN: an insertion of one unknown nucleotide (N) between positions 5 and 6
 * - c.5_6insN[10]: an insertion of 10 unknown nucleotides between positions 5 and 6
 * - c.5_6ins(50): an insertion of 50 nucleotides between positions 5 and 6
 * - c.5_6ins[NC_000023.10:g.32717298_32717411]: an insertion of a sequence from another
 *   reference between positions 5 and 6
 *
 * Notes:
 * - "ins" is the insertion designation
 * - two flanking nucleotides must be given (positions 5 and 6 in the examples)
 * - the inserted sequence is given after "ins"
 * - when the inserted sequence is very long, it can be given by its length in parentheses
 *
 * ============================================================================
 * DELETION-INSERTION (INDEL) (https://hgvs-nomenclature.org/stable/recommendations/DNA/delins/)
 * ============================================================================
 * Definition: a sequence change where, compared to a reference sequence, one or more
 * nucleotides are replaced by one or more other nucleotides and which is not a
 * substitution, inversion or conversion.
 *
 * Format: "prefix""position(s)_deleted""delins""inserted_sequence"
 *
 * Examples:
 * - c.6_8delinsAA: a deletion of nucleotides 6 to 8 (TGT) replaced by AA
 * - c.6delinsAA: a deletion of nucleotide 6 (T) replaced by AA
 * - c.6_10delins(5): a deletion of nucleotides 6 to 10 replaced by 5 nucleotides
 * - c.142_144delinsTGG: a deletion of nucleotides 142 to 144 (ATG) replaced by TGG
 *
 * Notes:
 * - "delins" is the deletion-insertion designation
 * - for all descriptions, the most 3' position possible is arbitrarily assigned to have
 *   been changed (3' rule)
 * - a deletion-insertion of one nucleotide for one other nucleotide is a substitution
 *
 * ============================================================================
 * INVERSION (https://hgvs-nomenclature.org/stable/recommendations/DNA/inversion/)
 * ============================================================================
 * Definition: a sequence change where, compared to a reference sequence, more than one
 * nucleotide replacing the original sequence is the reverse complement of the original
 * sequence.
 *
 * Format: "prefix""positions_inverted""inv"
 *
 * Examples:
 * - c.203_506inv: an inversion of nucleotides 203 to 506
 * - c.203_506inv304: an inversion of nucleotides 203 to 506, with the inverted sequence
 *   being 304 nucleotides long
 * - g.1077_1080inv: an inversion of nucleotides 1077 to 1080 (CTGA to TCAG)
 *
 * Notes:
 * - "inv" is the inversion designation
 * - by definition, an inversion involves more than one nucleotide
 * - inversions are described at the DNA level, not at the RNA or protein level
 *
 * ============================================================================
 * CONVERSION (https://hgvs-nomenclature.org/stable/recommendations/DNA/conversion/)
 * ============================================================================
 * Definition: a sequence change where, compared to a reference sequence, a range of
 * nucleotides is replaced by a sequence from elsewhere in the genome.
 *
 * Format: "prefix""positions_converted""con""positions_replacing_sequence"
 *
 * Examples:
 * - c.123_345con888_1110: a conversion of nucleotides 123 to 345 with nucleotides 888 to 1110
 * - NC_000022.10:g.42522624_42522669con42536337_42536382: a conversion in the CYP2D6 gene
 *
 * Notes:
 * - "con" is the conversion designation
 * - conversion is a special type of deletion-insertion where the inserted sequence
 *   originates from elsewhere in the genome
 *
 * ============================================================================
 * ALLELES (https://hgvs-nomenclature.org/stable/recommendations/DNA/alleles/)
 * ============================================================================
 * Definition: a series of variants on one chromosome (in cis) or on different chromosomes
 * (in trans).
 *
 * Format:
 * - Variants in cis: "prefix"["variant1";"variant2"]
 * - Variants in trans: "prefix"["variant1"];"prefix"["variant2"] or
 *   "prefix"["variant1"](;)"prefix"["variant2"] when phase is unknown
 *
 * Examples:
 * - c.[76A>C;83G>C]: two variants in the same allele (in cis)
 * - c.[76A>C];[83G>C]: two variants in different alleles (in trans)
 * - c.[76A>C](;)[83G>C]: two variants, unknown whether in cis or trans
 * - c.[76A>C;83G>C];[76A>C;83G>C]: homozygous for both variants
 * - c.[76A>C];[76=]: heterozygous, one allele has the variant, the other is wild type
 * - c.[76A>C];[?]: heterozygous, one allele has the variant, the other is unknown
 * - c.[76A>C;83G>C;101T>G]: three variants in the same allele
 *
 * Notes:
 * - square brackets [] are used to indicate alleles
 * - semicolons ; separate variants within an allele or between alleles
 * - parentheses around the semicolon (;) indicate unknown phase
 *
 * ============================================================================
 * REPEATED SEQUENCES (https://hgvs-nomenclature.org/stable/recommendations/DNA/repeated/)
 * ============================================================================
 * Definition: a sequence where, compared to a reference sequence, a segment of one or
 * more nucleotides (the repeat unit) is present several times, one after the other.
 *
 * Format: "prefix""position_repeat_unit""repeat_unit"["copy_number"]
 *
 * Examples:
 * - c.123CAG[23]: a repeat of 23 CAG units starting at position 123
 * - c.123_125[23]: a repeat of 23 copies of the sequence from position 123 to 125
 * - c.-128_-126[79]: a repeat of 79 CGG units in the 5' UTR (FMR1 gene)
 * - c.54GCA[23]: a repeat of 23 GCA units starting at position 54
 * - c.54GCA[19];[23]: compound heterozygous for 19 and 23 GCA repeats
 * - c.54GCA[(40_60)]: a repeat of 40 to 60 GCA units (uncertain number)
 *
 * Notes:
 * - the repeat unit is given in square brackets followed by the copy number
 * - when the exact copy number is not known, a range can be given in parentheses
 * - for compound heterozygotes, both alleles are given separated by semicolon
 *
 * ============================================================================
 * COMPLEX (https://hgvs-nomenclature.org/stable/recommendations/DNA/complex/)
 * ============================================================================
 * Definition: a sequence change where, compared to a reference sequence, a complex
 * rearrangement has occurred involving multiple types of changes.
 *
 * Examples:
 * - Complex rearrangements involving multiple breakpoints
 * - Chromothripsis events
 * - Complex insertions with inversions
 *
 * Notes:
 * - complex variants should be described as precisely as possible
 * - when a complex variant cannot be described using standard nomenclature,
 *   a description using multiple simple variants may be used
 *
 * ============================================================================
 * POSITION FORMATS
 * ============================================================================
 * - Simple: 123 (coding sequence position)
 * - Intronic: 123+5 (5 nucleotides into intron after position 123)
 *            123-10 (10 nucleotides into intron before position 123)
 * - UTR: *123 (3' UTR, 123 nucleotides after stop codon)
 *       -123 (5' UTR, 123 nucleotides before start codon)
 * - Uncertain: (123_456) (position is somewhere between 123 and 456)
 *             (?_123) (position is somewhere before 123)
 *             (123_?) (position is somewhere after 123)
 *
 * ============================================================================
 * GENERAL RULES
 * ============================================================================
 * - The prefix "c." indicates a coding DNA reference sequence
 * - The prefix "g." indicates a genomic reference sequence
 * - The prefix "m." indicates a mitochondrial reference sequence
 * - The prefix "n." indicates a non-coding DNA reference sequence
 * - All positions are relative to the reference sequence
 * - The 3' rule: for all descriptions, the most 3' position possible is arbitrarily
 *   assigned to have been changed
 * - Nucleotides are represented by their IUPAC codes: A, C, G, T (and N for unknown)
 */

// Position pattern: handles simple positions, intronic offsets, UTR positions
// Supports: 123, -123 (5' UTR), *123 (3' UTR), 123+5 (intronic), 123-10 (intronic)
const positionPattern = '(?:\\*|\\-)?\\d+(?:[\\+\\-]\\d+)?';

// Uncertain position pattern: supports (pos_pos), (?_pos), (pos_?), and simple positions
const uncertainPositionPattern = `(?:\\((?:\\?|${positionPattern})_(?:\\?|${positionPattern})\\)|${positionPattern})`;

// Range pattern: single position or range (pos_pos)
const rangePattern = `${uncertainPositionPattern}(?:_${uncertainPositionPattern})?`;

// Nucleotides: A, C, G, T, N (unknown) - case insensitive
const nucleotides = '[ACGTacgtNn]+';

// Build the complete cDNA pattern based on HGVS nomenclature
// Reference: https://hgvs-nomenclature.org/stable/recommendations/DNA/
const cdnaPatterns = [
  // Substitutions: c.123A>G, c.93+1G>T, c.*96G>A, c.-14G>A
  // Also supports: c.93G= (no change)
  `^c\\.${rangePattern}${nucleotides}(>|=)${nucleotides}?$`,

  // Deletions: c.10del, c.6_8del, c.6_8delGAT
  // Also supports uncertain ranges: c.(?_-30)_(12+1_13-1)del
  `^c\\.${rangePattern}del(${nucleotides})?$`,

  // Duplications: c.7dup, c.6_8dup, c.6_8dupTGT
  `^c\\.${rangePattern}dup(${nucleotides})?$`,

  // Insertions: c.5_6insT, c.5_6insGTCA, c.5_6ins10, c.5_6insN, c.5_6insN[10], c.5_6ins(50)
  // Also supports reference insertions: c.5_6ins[NC_000023.10:g.32717298_32717411]
  `^c\\.${rangePattern}ins(${nucleotides}|\\d+|\\(\\d+\\)|N\\[\\d+\\]|\\[.+\\])$`,

  // Deletion-insertions (indels): c.6_8delinsAA, c.6delinsAA, c.6_10delins(5)
  `^c\\.${rangePattern}delins(${nucleotides}|\\d+|\\(\\d+\\))$`,

  // Inversions: c.203_506inv, c.203_506inv304 (with length)
  `^c\\.${rangePattern}inv(\\d+)?$`,

  // Conversions: c.123_345con888_1110
  `^c\\.${rangePattern}con${rangePattern}$`,

  // Repeated sequences: c.123CAG[23], c.123_125[23], c.54GCA[(40_60)]
  // Also supports compound heterozygous: c.54GCA[19];[23]
  `^c\\.${rangePattern}(${nucleotides})?\\[(\\d+|\\(\\d+_\\d+\\))\\](;\\[\\d+\\])?$`,

  // Alleles in cis: c.[76A>C;83G>C], c.[76A>C;83G>C;101T>G]
  `^c\\.\\[.+\\]$`,

  // Alleles in trans: c.[76A>C];[83G>C], c.[76A>C](;)[83G>C]
  `^c\\.\\[.+\\](\\(;\\)|;)\\[.+\\]$`,

  // Wild type/no change: c.76=, c.76A=
  `^c\\.${rangePattern}(${nucleotides})?=$`,

  // Unknown variant: c.?
  `^c\\.\\?$`,

  // Mosaic/chimeric: c.123A>G/c.123A>T
  `^c\\.${rangePattern}${nucleotides}>${nucleotides}/.+$`,
];

// Combined pattern
const cdnaRegex = new RegExp(cdnaPatterns.join('|'), 'i');

/**
 * Check for duplicate copy-paste errors
 * Detects patterns like "c.1574C>T c.1574C>T" or "c.123del c.123del"
 */
const hasDuplicateCdna = (value: string): boolean => {
  if (!value) {
    return false;
  }

  // Check for repeated cDNA patterns separated by whitespace
  const trimmed = value.trim();

  // Split by whitespace and check for duplicates
  const parts = trimmed.split(/\s+/);
  if (parts.length > 1) {
    // If there are multiple parts, it's likely a copy-paste error
    const uniqueParts = new Set(parts);
    if (uniqueParts.size < parts.length) {
      return true; // Found duplicates
    }
    // Even if parts are different, multiple cDNA notations in one field is invalid
    if (parts.some((part) => part.startsWith('c.'))) {
      return parts.filter((part) => part.startsWith('c.')).length > 1;
    }
  }

  return false;
};

/**
 * Validate cDNA format according to HGVS nomenclature
 */
const isValidCdnaFormat = (value: string): boolean => {
  if (!value) {
    return false;
  }
  const trimmed = value.trim();
  return cdnaRegex.test(trimmed);
};

export const createVariantFormValidationSchema = () => {
  const fields: any = {
    gene_id: Yup.string().trim().required(requiredMessage).nullable(),
    transcript_primaryid: Yup.string()
      .trim()
      .required(requiredMessage)
      .nullable(),
    cdna: Yup.string()
      .trim()
      .required(requiredMessage)
      .matches(/^c\./i, cdnaPatternMessage) // explicit c.-prefix check
      .test(
        'no-duplicate-cdna', // duplicate check
        cdnaDuplicateMessage,
        (value) => !hasDuplicateCdna(value || ''),
      )
      .test('valid-hgvs-cdna', cdnaPatternMessage, (value) =>
        isValidCdnaFormat(value || ''),
      )
      .nullable(),
  };

  return Yup.object().shape(fields);
};
